import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startSystemUpdate, getSystemUpdateStatus, type UpdateJob } from '../../api/system';
import { getSocket } from '../../api/socket';
import { useSnackbar } from '../../context/SnackbarContext';

interface Props {
  open: boolean;
  onClose: () => void;
  packages: string[];
}

const CONFIRM_WORD = 'UPGRADE';
const CONFIRM_DIST = 'DIST-UPGRADE';

export function SystemUpdateDialog({ open, onClose, packages }: Props) {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();

  const [mode, setMode] = useState<'upgrade' | 'dist-upgrade'>('upgrade');
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [confirmText, setConfirmText] = useState('');
  const [job, setJob] = useState<UpdateJob | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const logBoxRef = useRef<HTMLDivElement>(null);

  const requiredConfirm = mode === 'dist-upgrade' ? CONFIRM_DIST : CONFIRM_WORD;
  const confirmOk = confirmText.trim() === requiredConfirm;

  useEffect(() => {
    if (!open) {
      setMode('upgrade');
      setExcluded(new Set());
      setConfirmText('');
      setJob(null);
      setLogs([]);
    }
  }, [open]);

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logs]);

  const mutation = useMutation({
    mutationFn: () => startSystemUpdate({ mode, exclude: [...excluded] }),
    onSuccess: ({ job: newJob }) => {
      setJob(newJob);
      setLogs([]);
      const socket = getSocket();
      socket.emit('system:update:subscribe', { job_id: newJob.id });
      socket.on('system:update:log', (data) => {
        if (data.job_id === newJob.id) {
          setLogs((prev) => [...prev, data.line]);
        }
      });
      void pollUntilDone(newJob.id);
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  async function pollUntilDone(jobId: string) {
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { job: latest } = await getSystemUpdateStatus();
        if (!latest || latest.id !== jobId) break;
        setJob({ ...latest });
        if (latest.state === 'done' || latest.state === 'failed') {
          getSocket().emit('system:update:unsubscribe', { job_id: jobId });
          qc.invalidateQueries({ queryKey: ['stats/updates'] });
          if (latest.state === 'done') {
            showSnackbar(`Update complete${latest.reboot_required ? ' — reboot required' : ''}`, 'success');
          } else {
            showSnackbar(`Update failed: ${latest.error ?? 'unknown error'}`, 'error');
          }
          break;
        }
      } catch {
        break;
      }
    }
  }

  function toggleExclude(pkg: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(pkg)) next.delete(pkg); else next.add(pkg);
      return next;
    });
  }

  const running = job && (job.state === 'queued' || job.state === 'updating' || job.state === 'upgrading');
  const finished = job && (job.state === 'done' || job.state === 'failed');

  return (
    <Dialog open={open} onClose={finished || !job ? onClose : undefined} maxWidth="md" fullWidth>
      <DialogTitle>Apply system updates</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={0.5}>

          {!job && (
            <>
              <Alert severity="warning">
                Running <strong>apt upgrade</strong> may briefly disrupt services. Schedule during a maintenance window.
              </Alert>

              {packages.length > 0 ? (
                <>
                  <Typography variant="subtitle2">
                    {packages.length} package{packages.length !== 1 ? 's' : ''} available
                    {excluded.size > 0 && ` (${excluded.size} excluded)`}
                  </Typography>
                  <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <List dense disablePadding>
                      {packages.map((pkg) => (
                        <ListItem key={pkg} disablePadding sx={{ px: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={excluded.has(pkg)}
                                onChange={() => toggleExclude(pkg)}
                              />
                            }
                            label={<Typography variant="caption" fontFamily="monospace">{pkg}</Typography>}
                            sx={{ mr: 0 }}
                          />
                          {excluded.has(pkg) && <Chip label="hold" size="small" color="warning" sx={{ ml: 'auto' }} />}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Checked packages will be held back (apt-mark hold) during this upgrade.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No packages listed (cache may be stale).</Typography>
              )}

              <Divider />

              <FormControl size="small">
                <InputLabel>Upgrade mode</InputLabel>
                <Select
                  value={mode}
                  label="Upgrade mode"
                  onChange={(e) => { setMode(e.target.value as typeof mode); setConfirmText(''); }}
                >
                  <MenuItem value="upgrade">upgrade — safe, keeps held packages</MenuItem>
                  <MenuItem value="dist-upgrade" sx={{ color: 'warning.main' }}>dist-upgrade — may remove packages</MenuItem>
                </Select>
              </FormControl>

              {mode === 'dist-upgrade' && (
                <Alert severity="error">
                  <strong>dist-upgrade</strong> can install and remove packages automatically. Use only if you know what you are doing.
                </Alert>
              )}

              <TextField
                size="small"
                label={`Type ${requiredConfirm} to confirm`}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                error={confirmText.length > 0 && !confirmOk}
                autoComplete="off"
              />
            </>
          )}

          {job && (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                {running && <CircularProgress size={16} />}
                <Chip
                  label={job.state.toUpperCase()}
                  color={job.state === 'done' ? 'success' : job.state === 'failed' ? 'error' : 'warning'}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">{job.mode} · {job.actor}</Typography>
              </Stack>

              <Box
                ref={logBoxRef}
                sx={{
                  bgcolor: 'grey.900',
                  borderRadius: 1,
                  p: 1.5,
                  height: 300,
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {logs.map((l, i) => (
                  <Box key={i} component="div" sx={{ color: l.startsWith('=== FAILED') ? 'error.main' : l.startsWith('===') ? 'primary.main' : 'text.primary' }}>
                    {l}
                  </Box>
                ))}
                {running && <Box sx={{ color: 'text.disabled' }}>…</Box>}
              </Box>

              {job.state === 'done' && job.reboot_required && (
                <Alert severity="warning">
                  A reboot is required to complete the update. Use the Dashboard reboot button.
                </Alert>
              )}
              {job.state === 'failed' && (
                <Alert severity="error">{job.error}</Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!running}>
          {finished ? 'Close' : 'Cancel'}
        </Button>
        {!job && (
          <Button
            variant="contained"
            color={mode === 'dist-upgrade' ? 'warning' : 'primary'}
            disabled={!confirmOk || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <CircularProgress size={16} /> : 'Start upgrade'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
